import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EditorContent, NodeViewContent, NodeViewProps, NodeViewWrapper, ReactNodeViewRenderer, useEditor } from '@tiptap/react';
import { Extension, Node, mergeAttributes } from '@tiptap/core';
import { NodeSelection, Plugin, PluginKey } from '@tiptap/pm/state';
import { Fragment, Slice } from '@tiptap/pm/model';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Image from '@tiptap/extension-image';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Collaboration from '@tiptap/extension-collaboration';
import CollaborationCursor from '@tiptap/extension-collaboration-cursor';
import GlobalDragHandle from 'tiptap-extension-global-drag-handle';
import type * as Y from 'yjs';
import type { Awareness } from 'y-protocols/awareness';
import { ySyncPluginKey, yUndoPluginKey } from 'y-prosemirror';

import http from '@/utils/http';
import * as wikiApi from '@/features/wiki/api';
import * as boardApi from '@/features/backlog/api';
import * as canvasApi from '@/features/canvas/api';
import { encodeDoc, REMOTE_ORIGIN } from '@/features/wiki/realtime';
import { WikiMedia, useWikiBlob } from '@/components/wiki-media';
import { WikiItemCard } from '@/components/wiki-item-card';
import { WhiteboardThumb } from '@/components/whiteboard-thumb';
import { SlashCommand } from '@/components/wiki-slash';
import { createWikiMention } from '@/components/wiki-mention';
import { WikiCommentMark } from '@/components/wiki-comment-mark';
import { Member } from '@/features/backlog/types';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/components/ui/cn';

export type SaveState = 'idle' | 'saving' | 'saved' | 'error';

interface BlockAuthor {
  createdByName?: string;
  createdAt?: string;
  authorName?: string;
  authorAt?: string;
}

// ---- toolbar icons (stroke-based, matching the app icon system) ----
const Ico = ({ children, size = 15 }: { children: React.ReactNode; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);
// Clean Lucide icon paths (24px, stroke, round) so the toolbar reads crisp at 15px.
const IcoBold = () => <Ico><path d="M6 12h9a4 4 0 0 1 0 8H7a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h6a4 4 0 0 1 0 8" /></Ico>;
const IcoItalic = () => <Ico><path d="M19 4h-9M14 20H5M15 4l-6 16" /></Ico>;
const IcoStrike = () => <Ico><path d="M16 4H9a3 3 0 0 0-2.83 4M14 12a4 4 0 0 1 0 8H6M4 12h16" /></Ico>;
const IcoCode = () => <Ico><path d="M16 18l6-6-6-6M8 6l-6 6 6 6" /></Ico>;
const IcoH1 = () => <Ico><path d="M4 12h8M4 18V6M12 18V6M17 12l3-2v8" /></Ico>;
const IcoH2 = () => <Ico><path d="M4 12h8M4 18V6M12 18V6M21 18h-4c0-4 4-3 4-6 0-1.5-2-2-3.5-1" /></Ico>;
const IcoH3 = () => <Ico><path d="M4 12h8M4 18V6M12 18V6M17.5 10.5c1.7-1 3.5 0 3.5 1.5a2 2 0 0 1-2 2M17 17.5c2 1.5 4 .3 4-1.5a2 2 0 0 0-2-2" /></Ico>;
const IcoBullet = () => <Ico><path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" /></Ico>;
const IcoRadio = () => <Ico><path d="M8 7h12M8 17h12" /><circle cx="4" cy="7" r="2.2" /><circle cx="4" cy="17" r="2.2" /><circle cx="4" cy="7" r="0.6" fill="currentColor" /></Ico>;
const IcoOrdered = () => <Ico><path d="M10 6h11M10 12h11M10 18h11M4 6h1v4M4 10h2M6 18H4c0-1 2-2 2-3s-1-1.5-2-1" /></Ico>;
const IcoTask = () => <Ico><path d="M3 7l2 2 4-4M3 17l2 2 4-4M13 6h8M13 12h8M13 18h8" /></Ico>;
const IcoIndent = () => <Ico><path d="M3 6h18M3 18h18M11 12h10M4 9l3 3-3 3" /></Ico>;
const IcoOutdent = () => <Ico><path d="M3 6h18M3 18h18M11 12h10M7 9l-3 3 3 3" /></Ico>;
const IcoQuote = () => <Ico><path d="M17 6H3M21 12H8M21 18H8M3 12v6" /></Ico>;
const IcoCodeBlock = () => <Ico><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M10 10l-2 2 2 2M14 10l2 2-2 2" /></Ico>;
const IcoLink = () => <Ico><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></Ico>;
const IcoComment = () => <Ico><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" /></Ico>;
const IcoTable = () => <Ico><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M12 3v18M3 9h18M3 15h18" /></Ico>;
const IcoImage = () => <Ico><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="9" cy="9" r="2" /><path d="M21 15l-3.09-3.09a2 2 0 0 0-2.82 0L6 21" /></Ico>;
const IcoEmbed = () => <Ico><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 9h18" /></Ico>;
const IcoProgress = () => <Ico><rect x="3" y="10" width="18" height="4" rx="2" /><rect x="3" y="10" width="9" height="4" rx="2" fill="currentColor" stroke="none" /></Ico>;
const IcoUndo = () => <Ico><path d="M9 14 4 9l5-5" /><path d="M4 9h10.5a5.5 5.5 0 0 1 0 11H10" /></Ico>;
const IcoRedo = () => <Ico><path d="m15 14 5-5-5-5" /><path d="M20 9H9.5a5.5 5.5 0 0 0 0 11H14" /></Ico>;
// Table-control icons (compact; used in the contextual sub-toolbar). A grid base + a directional cue.
const TIco = ({ children }: { children: React.ReactNode }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>
);
const IcoRowAbove = () => <TIco><rect x="4" y="11" width="16" height="9" rx="1.5" /><path d="M4 15.5h16M12 8V2.5M9.5 5 12 2.5 14.5 5" /></TIco>;
const IcoRowBelow = () => <TIco><rect x="4" y="4" width="16" height="9" rx="1.5" /><path d="M4 8.5h16M12 16v5.5M9.5 19l2.5 2.5 2.5-2.5" /></TIco>;
const IcoRowDel = () => <TIco><rect x="4" y="4" width="16" height="9" rx="1.5" /><path d="M4 8.5h16" /><path d="M9 18l6 3M15 18l-6 3" /></TIco>;
const IcoColLeft = () => <TIco><rect x="11" y="4" width="9" height="16" rx="1.5" /><path d="M15.5 4v16M8 12H2.5M5 9.5 2.5 12 5 14.5" /></TIco>;
const IcoColRight = () => <TIco><rect x="4" y="4" width="9" height="16" rx="1.5" /><path d="M8.5 4v16M16 12h5.5M19 9.5l2.5 2.5-2.5 2.5" /></TIco>;
const IcoColDel = () => <TIco><rect x="4" y="4" width="9" height="16" rx="1.5" /><path d="M8.5 4v16" /><path d="M18 9l3 6M21 9l-3 6" /></TIco>;
const IcoHeader = () => <TIco><rect x="3.5" y="4.5" width="17" height="15" rx="1.5" /><path d="M3.5 9.5h17" /><rect x="3.5" y="4.5" width="17" height="5" rx="1.5" fill="currentColor" opacity="0.28" stroke="none" /></TIco>;
const IcoMerge = () => <TIco><rect x="3.5" y="6" width="7.5" height="12" rx="1.2" /><rect x="13" y="6" width="7.5" height="12" rx="1.2" /><path d="M9 12h6" /></TIco>;
const IcoTableDel = () => <TIco><rect x="3.5" y="4.5" width="17" height="15" rx="2" /><path d="M8 9l8 6M16 9l-8 6" /></TIco>;

// ---- resizable, auth'd image node ----
function WikiImageView({ node, updateAttributes, selected, editor }: NodeViewProps) {
  const src = node.attrs.src as string;
  const width = (node.attrs.width as number | null) ?? null;
  const wrapRef = useRef<HTMLDivElement>(null);

  const onResizeStart = (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = wrapRef.current?.querySelector('img,video')?.getBoundingClientRect().width ?? width ?? 320;
    const max = wrapRef.current?.parentElement?.getBoundingClientRect().width ?? 760;
    const move = (ev: PointerEvent) => updateAttributes({ width: Math.round(Math.max(80, Math.min(startW + (ev.clientX - startX), max))) });
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  return (
    <NodeViewWrapper className="wiki-img" data-drag-handle>
      <div ref={wrapRef} className={cn('group relative inline-block max-w-full rounded-lg', selected && 'outline outline-2 outline-offset-2 outline-accent/70')}>
        {/* width null → natural size via CSS (never written through the edit path — a mere view must not autosave). */}
        <WikiMedia src={src} alt={node.attrs.alt as string | undefined} width={width} />
        {/* Only a drag-to-resize handle sits on the image itself; the size presets (S/M/L/Fit) live in
            the Image sub-toolbar under the main toolbar (shown while the image is selected), so no menu
            overlaps the image. */}
        {editor.isEditable ? (
          <span
            onPointerDown={onResizeStart}
            className="absolute right-0 top-1/2 h-10 w-2.5 -translate-y-1/2 translate-x-1/2 cursor-ew-resize rounded-full bg-brand opacity-0 ring-2 ring-surface transition-opacity group-hover:opacity-100"
            title="Drag to resize"
          />
        ) : null}
      </div>
    </NodeViewWrapper>
  );
}

const WikiImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: { default: null, parseHTML: (el) => (el.getAttribute('data-width') ? Number(el.getAttribute('data-width')) : null), renderHTML: (attrs) => (attrs.width ? { 'data-width': attrs.width } : {}) },
    };
  },
  addNodeView() {
    return ReactNodeViewRenderer(WikiImageView);
  },
});

// ---- any-file attachment node: PDF/HTML preview in a resizable frame, other types as a download card ----
function WikiFileView({ node, updateAttributes, selected, editor }: NodeViewProps) {
  const src = node.attrs.src as string;
  const name = (node.attrs.name as string) || 'File';
  const mime = (node.attrs.mime as string) || '';
  const width = (node.attrs.width as number | null) ?? null;
  const height = (node.attrs.height as number) ?? 460;
  const { url, error } = useWikiBlob(src);
  const isPdf = mime === 'application/pdf' || /\.pdf(\?|#|$)/i.test(name);
  const isHtml = mime.includes('html') || /\.html?(\?|#|$)/i.test(name);
  const preview = isPdf || isHtml;
  const wrapRef = useRef<HTMLDivElement>(null);

  const onResizeStart = (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startY = e.clientY;
    const startW = wrapRef.current?.getBoundingClientRect().width ?? width ?? 640;
    const maxW = wrapRef.current?.parentElement?.getBoundingClientRect().width ?? 760;
    const move = (ev: PointerEvent) =>
      updateAttributes({ width: Math.round(Math.max(220, Math.min(startW + (ev.clientX - startX), maxW))), height: Math.round(Math.max(180, height + (ev.clientY - startY))) });
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  if (!preview) {
    return (
      <NodeViewWrapper className="wiki-file" data-drag-handle>
        <a
          href={url ?? undefined}
          download={name}
          onClick={(e) => { if (!url) e.preventDefault(); }}
          contentEditable={false}
          className={cn('my-1.5 flex items-center gap-3 rounded-xl border border-line bg-surface-inset px-3.5 py-3 no-underline transition-colors hover:bg-layer', selected && 'outline outline-2 outline-offset-2 outline-accent/70')}
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-layer text-fg-muted ring-1 ring-line">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5" /></svg>
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[13px] font-medium text-fg-soft">{name}</span>
            <span className="block text-[11px] text-fg-faint">{mime || 'Attachment'}</span>
          </span>
          <span className="shrink-0 text-[11px] font-semibold text-accent">{url ? 'Download' : '…'}</span>
        </a>
      </NodeViewWrapper>
    );
  }
  return (
    <NodeViewWrapper className="wiki-file" data-drag-handle>
      <div ref={wrapRef} className={cn('group relative my-1.5 overflow-hidden rounded-xl border border-line bg-surface-inset', selected && 'outline outline-2 outline-offset-2 outline-accent/70')} style={{ width: width ? `${width}px` : '100%', maxWidth: '100%' }}>
        <div className="flex items-center justify-between gap-2 border-b border-line bg-surface px-3 py-1.5">
          <span className="min-w-0 flex-1 truncate text-[12px] font-medium text-fg-soft">{name}</span>
          {url ? <a href={url} download={name} className="shrink-0 text-[11px] font-semibold text-accent hover:underline" contentEditable={false}>Download</a> : null}
        </div>
        {error ? (
          <div className="p-4 text-[12px] text-fg-faint">Preview unavailable.</div>
        ) : url ? (
          <iframe src={url} title={name} sandbox={isHtml ? 'allow-same-origin allow-popups' : undefined} className="block w-full bg-white" style={{ height: `${height}px` }} />
        ) : (
          <div className="flex items-center justify-center p-8"><Spinner className="h-4 w-4 text-fg-faint" /></div>
        )}
        {editor.isEditable ? <span onPointerDown={onResizeStart} className="absolute bottom-1.5 right-1.5 h-4 w-4 cursor-nwse-resize rounded bg-brand opacity-0 ring-2 ring-surface transition-opacity group-hover:opacity-100" title="Drag to resize" /> : null}
      </div>
    </NodeViewWrapper>
  );
}

const WikiFile = Node.create({
  name: 'wikiFile',
  group: 'block',
  atom: true,
  selectable: true,
  addAttributes() {
    return { src: { default: '' }, name: { default: '' }, mime: { default: '' }, width: { default: null }, height: { default: 460 } };
  },
  parseHTML() {
    return [{ tag: 'div[data-wiki-file]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-wiki-file': '' })];
  },
  addNodeView() {
    return ReactNodeViewRenderer(WikiFileView);
  },
});

// ---- board-item / whiteboard embed card ----
function WikiEmbedView({ node, selected }: NodeViewProps) {
  const kind = node.attrs.kind as string;
  const label = (node.attrs.label as string) || 'Untitled';
  const href = node.attrs.href as string;
  const refId = node.attrs.refId as string;
  const sel = selected ? 'rounded-xl outline outline-2 outline-offset-2 outline-accent/70' : '';
  // Board items (incl. those found via search) render as a rich, read-only kanban-style card.
  if (kind === 'item' && refId) {
    return (
      <NodeViewWrapper className={cn('wiki-embed', sel)} data-drag-handle>
        <WikiItemCard itemId={refId} fallbackLabel={label} href={href} />
      </NodeViewWrapper>
    );
  }
  // Whiteboards render as a card with their preview thumbnail.
  if (kind === 'whiteboard' && refId) {
    return (
      <NodeViewWrapper className={cn('wiki-embed', sel)} data-drag-handle>
        <div contentEditable={false} className="my-1.5 overflow-hidden rounded-xl border border-line bg-surface-raised transition-colors hover:border-line-strong">
          <WhiteboardThumb whiteboardId={refId} className="h-44 w-full border-b border-line" />
          <a href={href} data-wiki-embed="" onClick={(e) => { e.preventDefault(); if (href) window.open(href, '_blank', 'noopener,noreferrer'); }} className="flex items-center gap-2.5 px-3 py-2 text-[13px] no-underline">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-violet/15 text-[11px] font-bold text-violet">▦</span>
            <span className="min-w-0 flex-1 truncate font-medium text-fg-soft">{label}</span>
            <span className="shrink-0 text-[11px] uppercase tracking-wide text-fg-faint">whiteboard</span>
          </a>
        </div>
      </NodeViewWrapper>
    );
  }
  return (
    <NodeViewWrapper className={cn('wiki-embed', sel)} data-drag-handle>
      <a
        href={href}
        data-wiki-embed=""
        onClick={(e) => { e.preventDefault(); if (href) window.open(href, '_blank', 'noopener,noreferrer'); }}
        className="my-1 flex items-center gap-2.5 rounded-lg border border-line bg-surface-inset px-3 py-2 text-[13px] no-underline transition-colors hover:border-line-strong hover:bg-layer"
        contentEditable={false}
      >
        <span className={cn('flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[11px] font-bold', kind === 'whiteboard' ? 'bg-violet/15 text-violet' : 'bg-brand/15 text-brand')}>{kind === 'whiteboard' ? '▦' : '#'}</span>
        <span className="min-w-0 flex-1 truncate font-medium text-fg-soft">{label}</span>
        <span className="shrink-0 text-[11px] uppercase tracking-wide text-fg-faint">{kind}</span>
      </a>
    </NodeViewWrapper>
  );
}

const WikiEmbed = Node.create({
  name: 'wikiEmbed',
  group: 'block',
  atom: true,
  selectable: true,
  addAttributes() {
    return { kind: { default: 'item' }, refId: { default: '' }, label: { default: '' }, href: { default: '' } };
  },
  parseHTML() {
    return [{ tag: 'div[data-wiki-embed]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-wiki-embed': '' })];
  },
  addNodeView() {
    return ReactNodeViewRenderer(WikiEmbedView);
  },
});

// ---- checklist completion widget (a live progress bar over the page's task items) ----
function ChecklistProgressView({ editor, selected }: NodeViewProps) {
  const [, force] = useState(0);
  useEffect(() => {
    const cb = () => force((n) => n + 1);
    editor.on('update', cb); // recount whenever a checkbox is toggled anywhere on the page
    return () => { editor.off('update', cb); };
  }, [editor]);
  let total = 0;
  let done = 0;
  editor.state.doc.descendants((node) => {
    if (node.type.name === 'taskItem') {
      total += 1;
      if (node.attrs.checked) done += 1;
    }
  });
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <NodeViewWrapper className="wiki-progress" contentEditable={false}>
      <div className={cn('my-2 select-none rounded-xl border bg-surface-inset px-3.5 py-3', selected ? 'border-accent/70 outline outline-2 outline-offset-2 outline-accent/70' : 'border-line')}>
        <div className="h-2.5 w-full overflow-hidden rounded-full bg-layer-strong">
          <div className="h-full rounded-full bg-ok transition-[width] duration-300" style={{ width: `${pct}%` }} />
        </div>
        <div className="mt-2 text-[12.5px] font-medium text-fg-muted">
          {total ? `${done} of ${total} tasks completed (${pct}%)` : 'Add checklists to this page to track completion here.'}
        </div>
      </div>
    </NodeViewWrapper>
  );
}

const ChecklistProgress = Node.create({
  name: 'checklistProgress',
  group: 'block',
  atom: true,
  selectable: true,
  parseHTML() {
    return [{ tag: 'div[data-checklist-progress]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-checklist-progress': '' })];
  },
  addNodeView() {
    return ReactNodeViewRenderer(ChecklistProgressView);
  },
});

// ---- drag-to-toggle checkboxes: press a checkbox and drag across others to flip them all to the
//      SAME new state (like a spreadsheet fill). The initial click keeps its native toggle. ----
const CheckboxDragPaint = Extension.create({
  name: 'checkboxDragPaint',
  addProseMirrorPlugins() {
    let painting = false;
    let target = false;

    const taskItemAt = (view: import('@tiptap/pm/view').EditorView, dom: HTMLElement | null) => {
      const li = dom?.closest?.('li[data-checked]') as HTMLElement | null;
      if (!li) return null;
      const $pos = view.state.doc.resolve(view.posAtDOM(li, 0));
      for (let d = $pos.depth; d >= 0; d--) {
        if ($pos.node(d).type.name === 'taskItem') return { node: $pos.node(d), before: $pos.before(d) };
      }
      return null;
    };
    const paint = (view: import('@tiptap/pm/view').EditorView, dom: HTMLElement | null) => {
      const t = taskItemAt(view, dom);
      if (!t || t.node.attrs.checked === target) return;
      view.dispatch(view.state.tr.setNodeMarkup(t.before, undefined, { ...t.node.attrs, checked: target }));
    };

    return [
      new Plugin({
        props: {
          handleDOMEvents: {
            pointerdown: (view, event) => {
              const el = event.target as HTMLElement | null;
              // Only start when pressing the checkbox (its label / input / styled box), not the row text.
              // The task-item <li> carries data-checked (NOT data-type="taskItem") inside ul[data-type=taskList].
              if (!el?.closest?.('li[data-checked] > label')) return false;
              const t = taskItemAt(view, el);
              if (!t) return false;
              target = !t.node.attrs.checked; // the state the first item is about to become
              painting = true;
              // Track the drag on WINDOW capture-phase listeners — more reliable than ProseMirror's
              // pointermove routing once the pointer leaves the pressed checkbox.
              const move = (e: PointerEvent) => {
                if (!painting) return;
                const under = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
                if (under?.closest?.('li[data-checked]')) {
                  paint(view, under);
                  e.preventDefault();
                }
              };
              const stop = () => {
                painting = false;
                window.removeEventListener('pointermove', move, true);
                window.removeEventListener('pointerup', stop, true);
              };
              window.addEventListener('pointermove', move, true);
              window.addEventListener('pointerup', stop, true);
              return false; // let the native checkbox toggle the first item
            },
          },
        },
      }),
    ];
  },
});

// ---- block indent (paragraphs & headings) — a Word/Docs-style left-margin indent LEVEL ----
// ProseMirror has no native paragraph indent, so we carry an `indent` level (0..MAX) as a global
// attribute on paragraph/heading and render it as a left margin. `indentBlocks`/`outdentBlocks` bump
// EVERY block in the selection, so a multi-block selection indents together. Lists are separate —
// there Tab nests the item (see ListKeymap). The attribute is in the schema so it round-trips through
// the Yjs doc + content snapshot and renders in the read-only/revision views too.
const INDENT_MAX = 8; // must match the per-level margin rules in index.scss (.md-content [data-indent="N"])
const INDENT_TYPES = ['paragraph', 'heading'];

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    indent: {
      indentBlocks: () => ReturnType;
      outdentBlocks: () => ReturnType;
    };
  }
}

const Indent = Extension.create({
  name: 'indent',
  addGlobalAttributes() {
    return [
      {
        types: INDENT_TYPES,
        attributes: {
          indent: {
            default: 0,
            parseHTML: (el) => {
              const v = parseInt(el.getAttribute('data-indent') || '0', 10);
              return Number.isNaN(v) ? 0 : Math.min(INDENT_MAX, Math.max(0, v));
            },
            // Rendered as data-indent + CSS (not an inline margin) so the margin can be zeroed inside
            // list items — there the list nesting owns the indentation. See index.scss.
            renderHTML: (attrs) => (attrs.indent ? { 'data-indent': String(attrs.indent) } : {}),
          },
        },
      },
    ];
  },
  addCommands() {
    const shift = (delta: number) => ({ state, dispatch }: { state: import('@tiptap/pm/state').EditorState; dispatch?: (tr: import('@tiptap/pm/state').Transaction) => void }) => {
      const { from, to } = state.selection;
      const tr = state.tr;
      let changed = false;
      state.doc.nodesBetween(from, to, (node, pos) => {
        if (!INDENT_TYPES.includes(node.type.name)) return true;
        const cur = (node.attrs.indent as number) || 0;
        const next = Math.min(INDENT_MAX, Math.max(0, cur + delta));
        if (next !== cur) {
          tr.setNodeMarkup(pos, undefined, { ...node.attrs, indent: next });
          changed = true;
        }
        return false; // don't descend into the block
      });
      if (changed && dispatch) dispatch(tr.scrollIntoView());
      return changed;
    };
    return { indentBlocks: () => shift(1), outdentBlocks: () => shift(-1) };
  },
});

// ---- Tab / Shift-Tab / ⌘] / ⌘[ indent + outdent (list-aware) + word-processor list edits ----
// One place that routes indent/outdent to the right behaviour: inside a list, indent NESTS the item
// (sink) and outdent un-nests it (lift); everywhere else it changes the block indent level (Indent
// above). Tab / Shift-Tab and ⌘] / ⌘[ are equivalent, so it works "standardized" like other editors.
// Keys are CONSUMED inside the editor so Tab never escapes and steals focus — EXCEPT in tables (Tab
// moves between cells) and code blocks (leave their own handling). Backspace at the start of an empty
// list item, or an indented paragraph, outdents cleanly instead of the default merge. Registered AFTER
// StarterKit so it wins the shared Tab/Backspace keys; Enter is left to TipTap's splitListItem.
const ListKeymap = Extension.create({
  name: 'listKeymap',
  addKeyboardShortcuts() {
    const ed = this.editor;
    const inList = () => ed.isActive('listItem') || ed.isActive('taskItem');
    const indent = () => {
      if (inList()) return ed.commands.sinkListItem('listItem') || ed.commands.sinkListItem('taskItem') || true;
      return ed.commands.indentBlocks() || true; // consume even at max, so Tab never escapes
    };
    const outdent = () => {
      if (inList()) return ed.commands.liftListItem('listItem') || ed.commands.liftListItem('taskItem') || true;
      return ed.commands.outdentBlocks() || true;
    };
    return {
      Tab: () => (ed.isActive('table') || ed.isActive('codeBlock') ? false : indent()),
      'Shift-Tab': () => (ed.isActive('table') || ed.isActive('codeBlock') ? false : outdent()),
      'Mod-]': () => indent(),
      'Mod-[': () => outdent(),
      // Backspace at the very START of an INDENTED PARAGRAPH (not in a list): drop one indent level
      // first, like a word processor. Lists are deliberately left to TipTap's default joinBackward,
      // which deletes/merges an empty item cleanly — custom lifting here splits the list and makes
      // items impossible to delete.
      Backspace: () => {
        const { selection } = ed.state;
        if (!selection.empty || inList()) return false;
        const { $from } = selection;
        if ($from.parentOffset !== 0) return false;
        if ((($from.parent.attrs.indent as number) || 0) > 0) return ed.commands.outdentBlocks();
        return false;
      },
    };
  },
});

// ---- authorship stamping (powers the right-click "who created / last edited") ----
const AUTHOR_TYPES = ['paragraph', 'heading', 'codeBlock', 'blockquote', 'listItem', 'taskItem', 'tableCell', 'tableHeader'];

const Authorship = Extension.create<{ user: { id: string; name: string } }>({
  name: 'authorship',
  addOptions() {
    return { user: { id: '', name: '' } };
  },
  addGlobalAttributes() {
    const attr = { default: null as string | null, rendered: false };
    return [{ types: AUTHOR_TYPES, attributes: { author: attr, authorName: attr, authorAt: attr, createdBy: attr, createdByName: attr, createdAt: attr } }];
  },
  addProseMirrorPlugins() {
    const user = this.options.user;
    return [
      new Plugin({
        appendTransaction: (trs, _old, newState) => {
          if (!user.id) return null;
          if (!trs.some((t) => t.docChanged)) return null;
          if (trs.some((t) => t.getMeta(ySyncPluginKey))) return null; // remote change — author already set
          const { $from } = newState.selection;
          if ($from.depth < 1) return null;
          const parent = $from.parent;
          if (!AUTHOR_TYPES.includes(parent.type.name)) return null;
          if (parent.attrs.author === user.id) return null; // already ours
          const pos = $from.before($from.depth);
          const now = new Date().toISOString();
          const patch: Record<string, unknown> = { ...parent.attrs, author: user.id, authorName: user.name, authorAt: now };
          if (!parent.attrs.createdBy) {
            patch.createdBy = user.id;
            patch.createdByName = user.name;
            patch.createdAt = now;
          }
          return newState.tr.setNodeMarkup(pos, undefined, patch).setMeta('addToHistory', false);
        },
      }),
    ];
  },
});

// ---- radio group: single-choice options (like a checklist, but picking one clears the siblings) ----
function RadioItemView({ node, editor, getPos }: NodeViewProps) {
  const checked = !!node.attrs.checked;
  const pick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!editor.isEditable || typeof getPos !== 'function') return;
    const pos = getPos();
    if (pos == null) return;
    const { state } = editor;
    const $pos = state.doc.resolve(pos);
    const tr = state.tr;
    if ($pos.parent?.type.name === 'radioGroup') {
      // exactly one selected: set this item checked, clear every sibling in the group
      const start = $pos.start();
      $pos.parent.forEach((child, offset) => {
        if (child.type.name === 'radioItem') tr.setNodeMarkup(start + offset, undefined, { ...child.attrs, checked: start + offset === pos });
      });
    } else {
      tr.setNodeMarkup(pos, undefined, { ...node.attrs, checked: !checked });
    }
    editor.view.dispatch(tr);
  };
  return (
    <NodeViewWrapper as="li" className="wiki-radio-item" data-type="radioItem" data-checked={checked ? 'true' : 'false'}>
      <span className="wiki-radio-btn" contentEditable={false} onMouseDown={pick} role="radio" aria-checked={checked}>
        <span className="wiki-radio-dot" />
      </span>
      <NodeViewContent className="wiki-radio-content" />
    </NodeViewWrapper>
  );
}

const RadioItem = Node.create({
  name: 'radioItem',
  content: 'paragraph block*',
  defining: true,
  addAttributes() {
    return {
      checked: {
        default: false,
        parseHTML: (el) => el.getAttribute('data-checked') === 'true',
        renderHTML: (attrs) => ({ 'data-checked': attrs.checked ? 'true' : 'false' }),
      },
    };
  },
  parseHTML() {
    return [{ tag: 'li[data-type="radioItem"]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['li', mergeAttributes(HTMLAttributes, { 'data-type': 'radioItem' }), 0];
  },
  addNodeView() {
    return ReactNodeViewRenderer(RadioItemView);
  },
});

const RadioGroup = Node.create({
  name: 'radioGroup',
  group: 'block',
  content: 'radioItem+',
  parseHTML() {
    return [{ tag: 'ul[data-type="radioGroup"]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['ul', mergeAttributes(HTMLAttributes, { 'data-type': 'radioGroup', class: 'wiki-radio-group' }), 0];
  },
});

/** A fresh radio group with two empty options (used by the toolbar + "/" menu). */
const RADIO_GROUP_JSON = {
  type: 'radioGroup',
  content: [
    { type: 'radioItem', attrs: { checked: true }, content: [{ type: 'paragraph' }] },
    { type: 'radioItem', attrs: { checked: false }, content: [{ type: 'paragraph' }] },
  ],
};

// Auto-scroll the page while dragging a block (the global drag handle uses native HTML5 drag, which does
// NOT auto-scroll). When the pointer nears the top/bottom edge of the editor's scroll container, a rAF
// loop scrolls it — proportionally to how close to the edge, and continuing while held at the edge — so
// you can drop a block anywhere in a long page.
const AutoScrollOnDrag = Extension.create({
  name: 'autoScrollOnDrag',
  addProseMirrorPlugins() {
    let raf = 0;
    let lastY = 0;
    let scroller: HTMLElement | null = null;
    const findScroller = (dom: HTMLElement): HTMLElement | null => {
      let el: HTMLElement | null = dom.parentElement;
      while (el) {
        const oy = getComputedStyle(el).overflowY;
        if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight + 4) return el;
        el = el.parentElement;
      }
      return null;
    };
    const step = () => {
      raf = 0;
      if (!scroller) return;
      const EDGE = 84;
      const MAX = 16;
      const r = scroller.getBoundingClientRect();
      let d = 0;
      if (lastY < r.top + EDGE) d = -Math.ceil(((r.top + EDGE - lastY) / EDGE) * MAX);
      else if (lastY > r.bottom - EDGE) d = Math.ceil(((lastY - (r.bottom - EDGE)) / EDGE) * MAX);
      if (d !== 0) {
        scroller.scrollTop += d;
        raf = requestAnimationFrame(step);
      }
    };
    const stop = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
      scroller = null;
    };
    return [
      new Plugin({
        props: {
          handleDOMEvents: {
            dragover: (view, event) => {
              lastY = (event as DragEvent).clientY;
              if (!scroller) scroller = findScroller(view.dom);
              if (scroller && !raf) raf = requestAnimationFrame(step);
              return false;
            },
            drop: () => {
              stop();
              return false;
            },
            dragend: () => {
              stop();
              return false;
            },
          },
        },
      }),
    ];
  },
});

// The selection RING on an inline mention chip. Inline-atom React node views neither receive a working
// `selected` prop NOR keep a decoration class (React re-renders overwrite it). So we toggle the class
// IMPERATIVELY on the node's real DOM via a plugin view — bulletproof, and it survives re-renders because
// `update` runs after every transaction (incl. selection changes) and re-applies.
const SelectionRing = Extension.create({
  name: 'selectionRing',
  addProseMirrorPlugins() {
    return [
      new Plugin({
        view() {
          let prev: Element | null = null;
          const clear = () => {
            if (prev) {
              prev.classList.remove('wiki-mention-selected');
              prev = null;
            }
          };
          return {
            update(view) {
              clear();
              // Duck-type the NodeSelection: `instanceof` can silently fail when prosemirror-state is
              // duplicated in the bundle (a NodeSelection has `.node`; text/all selections don't).
              const sel = view.state.selection as unknown as { node?: { type: { name: string } }; from: number };
              if (!sel.node || sel.node.type.name !== 'mention') return;
              const dom = view.nodeDOM(sel.from);
              if (!dom || dom.nodeType !== 1) return;
              const host = dom as HTMLElement;
              // Land the ring on the actual chip pill (where the styling lives), wherever nodeDOM points.
              const target = (host.classList.contains('wiki-mention') ? host : host.querySelector('.wiki-mention') || host.closest('.wiki-mention') || host) as HTMLElement;
              target.classList.add('wiki-mention-selected');
              prev = target;
            },
            destroy: clear,
          };
        },
      }),
    ];
  },
});

// Comment-mark styling: the base amber highlight is on the mark (.wiki-comment-mark via renderHTML); this
// decoration adds `wiki-comment-active` (the thread selected in the rail) / `wiki-comment-resolved` on top.
// It reads the live active-id + resolved-set through getters (refs) so the plugin never re-creates; the
// editor dispatches a no-op refresh transaction when either changes (decorations recompute per transaction).
function createCommentDeco(getActive: () => string | null | undefined, getResolved: () => Set<string> | undefined) {
  return Extension.create({
    name: 'commentDeco',
    addProseMirrorPlugins() {
      return [
        new Plugin({
          props: {
            decorations(state) {
              const active = getActive();
              const resolved = getResolved();
              const decos: Decoration[] = [];
              state.doc.descendants((node, pos) => {
                if (!node.isText) return;
                const mk = node.marks.find((m) => m.type.name === 'commentThread');
                const id = mk?.attrs.threadId as string | undefined;
                if (!id) return;
                let cls = '';
                if (resolved?.has(id)) cls = 'wiki-comment-resolved';
                else if (active && id === active) cls = 'wiki-comment-active';
                if (cls) decos.push(Decoration.inline(pos, pos + node.nodeSize, { class: cls }));
              });
              return DecorationSet.create(state.doc, decos);
            },
          },
        }),
      ];
    },
  });
}

// ---- pending (unsubmitted) comment highlight ----
// The highlight for a comment being DRAFTED lives here as a view-only decoration, never as a mark in
// the collaborative doc. So an abandoned draft — rail closed, another thread started, page left, tab
// closed — leaves NOTHING behind (no stray highlight, nothing autosaved). It becomes a real, persisted
// `commentThread` mark only when the comment is actually submitted (commitPending, on server success).
const pendingCommentKey = new PluginKey<{ from: number; to: number } | null>('wikiPendingComment');
const PendingComment = Extension.create({
  name: 'pendingComment',
  addProseMirrorPlugins() {
    return [
      new Plugin<{ from: number; to: number } | null>({
        key: pendingCommentKey,
        state: {
          init: () => null,
          apply(tr, value) {
            const meta = tr.getMeta(pendingCommentKey) as { from: number; to: number } | null | undefined;
            if (meta !== undefined) return meta; // explicit set (start) / clear (commit / cancel)
            if (!value) return value;
            // Keep the pending range on the right text as the doc changes (local AND remote edits).
            const from = tr.mapping.map(value.from);
            const to = tr.mapping.map(value.to);
            return to > from ? { from, to } : null;
          },
        },
        props: {
          decorations(state) {
            const range = pendingCommentKey.getState(state);
            if (!range) return null;
            return DecorationSet.create(state.doc, [Decoration.inline(range.from, range.to, { class: 'wiki-comment-mark wiki-comment-pending' })]);
          },
        },
      }),
    ];
  },
});

interface Props {
  pageId: string;
  ydoc: Y.Doc;
  awareness: Awareness;
  user: { id: string; name: string; color: string };
  members: Member[];
  onSaveState?: (s: SaveState) => void;
  onAttachmentAdded?: () => void;
  restoreRef?: React.MutableRefObject<((content: unknown) => void) | null>;
  /** ProseMirror JSON to materialize into the (empty) collaborative doc ONCE — for a page IMPORTED with
   *  body content but no Yjs doc_state yet. Only applied when the loaded doc is genuinely empty. */
  seedContent?: unknown;
  // ---- anchored comments ----
  activeThreadId?: string | null;
  resolvedThreadIds?: Set<string>;
  onActivateThread?: (threadId: string) => void;
  onRequestNewThread?: (threadId: string, quote: string) => void;
  commentCmdRef?: React.MutableRefObject<WikiCommentCmd | null>;
}

/** Imperative comment commands the page can call (add/remove a mark, scroll to a marked range). */
export interface WikiCommentCmd {
  addMark: (id: string) => void;
  removeMark: (id: string) => void;
  scrollTo: (id: string) => void;
  /** Turn the current pending (view-only) highlight into a real persisted comment mark — on submit. */
  commitPending: (id: string) => void;
  /** Drop the pending highlight with no trace — on cancel / abandon. */
  clearPending: () => void;
}

/**
 * The collaborative wiki page editor: TipTap over a shared Yjs document (concurrent editing + live
 * carets), with tables (+ their own control menu), task-list checkboxes, a "/" widget menu, resizable
 * auth'd media, board/whiteboard embeds, a per-block drag handle, a word-processor right-click menu,
 * per-block authorship, and debounced autosave. No content prop — the document is hydrated from `ydoc`.
 */
export default function WikiEditor({ pageId, ydoc, awareness, user, members, onSaveState, onAttachmentAdded, restoreRef, activeThreadId, resolvedThreadIds, onActivateThread, onRequestNewThread, commentCmdRef, seedContent }: Props) {
  const [uploading, setUploading] = useState(0);
  const [ctx, setCtx] = useState<{ x: number; y: number; blame: BlockAuthor } | null>(null);
  const [embedOpen, setEmbedOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const saveTimer = useRef<number | undefined>(undefined);
  const dirty = useRef(false);
  const provider = useMemo(() => ({ awareness }), [awareness]);
  // The context-menu opener, called from a ProseMirror DOM handler so a right-click on ANY node —
  // including contentEditable=false node views (images, embed cards, checkboxes) — shows our menu.
  const showCtxRef = useRef<((x: number, y: number) => void) | null>(null);
  const [linkModal, setLinkModal] = useState<{ url: string } | null>(null);
  const [hoverRow, setHoverRow] = useState<{ top: number; left: number; from: number; to: number } | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const [gutterBubbles, setGutterBubbles] = useState<{ id: string; top: number; left: number; resolved: boolean; active: boolean }[]>([]);
  // Members read live by the @mention autocomplete, so the extension never re-creates on roster change.
  const membersRef = useRef(members);
  membersRef.current = members;
  // Comment refs — read live by the decoration plugin + click handler without re-creating the editor.
  const activeThreadRef = useRef(activeThreadId);
  activeThreadRef.current = activeThreadId;
  const resolvedRef = useRef(resolvedThreadIds);
  resolvedRef.current = resolvedThreadIds;
  const onActivateThreadRef = useRef(onActivateThread);
  onActivateThreadRef.current = onActivateThread;
  const onRequestNewThreadRef = useRef(onRequestNewThread);
  onRequestNewThreadRef.current = onRequestNewThread;

  const editor = useEditor(
    {
      extensions: [
        StarterKit.configure({ history: false, heading: { levels: [1, 2, 3] }, codeBlock: { HTMLAttributes: { class: 'md-code' } } }),
        Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { target: '_blank', rel: 'noreferrer noopener', class: 'md-link' } }),
        WikiImage,
        WikiFile,
        WikiEmbed,
        ChecklistProgress,
        RadioGroup,
        RadioItem,
        createWikiMention(() => membersRef.current),
        Table.configure({ resizable: true, HTMLAttributes: { class: 'wiki-table' } }),
        TableRow,
        TableHeader,
        TableCell,
        TaskList,
        TaskItem.configure({ nested: true }),
        Indent,
        ListKeymap,
        CheckboxDragPaint,
        SelectionRing,
        AutoScrollOnDrag,
        WikiCommentMark.configure({ onActivate: (id) => onActivateThreadRef.current?.(id) }),
        PendingComment,
        createCommentDeco(() => activeThreadRef.current, () => resolvedRef.current),
        GlobalDragHandle.configure({ dragHandleWidth: 22, scrollTreshold: 100 }),
        Placeholder.configure({ placeholder: 'Write something, or type "/" to insert a widget…' }),
        Collaboration.configure({ document: ydoc }),
        CollaborationCursor.configure({ provider, user: { name: user.name, color: user.color } }),
        Authorship.configure({ user: { id: user.id, name: user.name } }),
        SlashCommand.configure({ onImage: () => fileRef.current?.click(), onEmbed: () => setEmbedOpen(true) }),
      ],
      editorProps: {
        attributes: { class: 'md-content wiki-content wiki-col' },
        // Undo granularity: the Yjs UndoManager merges every change made within its captureTimeout
        // into ONE step, so a continuous typing burst would otherwise undo all the way back to the
        // first character. Close the current undo group at word/line boundaries (space, Enter) so undo
        // steps back word-by-word like a normal editor. Returning false leaves the key to normal handling.
        handleKeyDown: (view, event) => {
          if (event.key === ' ' || event.key === 'Enter') {
            yUndoPluginKey.getState(view.state)?.undoManager?.stopCapturing();
          }
          return false;
        },
        transformPastedHTML: mapPastedHtml,
        // Strip comment-anchor marks from pasted content: a thread belongs to its source page, so a copied
        // highlight would be orphaned (referencing a thread that isn't on this page). Also protects against
        // duplicating a highlight when copying within the same page.
        transformPasted: (slice, view) => {
          const type = view.state.schema.marks.commentThread;
          if (!type) return slice;
          const strip = (fragment: Fragment): Fragment => {
            const out: import('@tiptap/pm/model').Node[] = [];
            fragment.forEach((node) => {
              let n = node;
              if (n.isText && n.marks.some((m) => m.type === type)) n = n.mark(n.marks.filter((m) => m.type !== type));
              if (n.content && n.content.size) n = n.copy(strip(n.content));
              out.push(n);
            });
            return Fragment.fromArray(out);
          };
          return new Slice(strip(slice.content), slice.openStart, slice.openEnd);
        },
        handleDOMEvents: {
          // Own the right-click on everything inside the editor (text AND node views) so the native
          // browser menu never shows — a plain onContextMenu prop misses contentEditable=false nodes.
          contextmenu: (_view, event) => {
            const e = event as MouseEvent;
            e.preventDefault();
            showCtxRef.current?.(e.clientX, e.clientY);
            return true;
          },
          // The block drag handle sets view.dragging.move = event.ctrlKey, so a plain drag COPIES the
          // node (duplicating tables/images). Force MOVE by default (Ctrl/Cmd/Alt still copies), so
          // repositioning a block moves it instead of duplicating it.
          dragover: (view, event) => {
            const e = event as DragEvent;
            if (view.dragging && !e.ctrlKey && !e.metaKey && !e.altKey) view.dragging = { ...view.dragging, move: true };
            return false;
          },
        },
        handleClick: (view, pos, event) => {
          // A comment-marked range → activate its thread in the rail (checked before link handling).
          const $pos = view.state.doc.resolve(pos);
          const mk = $pos.marks().find((m) => m.type.name === 'commentThread');
          const tid = mk?.attrs.threadId as string | undefined;
          if (tid) {
            onActivateThreadRef.current?.(tid);
            return true;
          }
          const a = (event.target as HTMLElement | null)?.closest?.('a');
          const href = a?.getAttribute('href');
          // Skip embed cards — their node view handles the click (else the link opens in TWO tabs).
          if (a && href && !a.closest('.wiki-embed')) {
            window.open(href, '_blank', 'noopener,noreferrer');
            return true;
          }
          return false;
        },
        // Clicking an inline atom (a @mention) drops the caret beside it by default — node-select it
        // instead so it gets the selected ring and can be deleted, like every block element.
        handleClickOn: (view, _pos, node, nodePos, _event, direct) => {
          if (direct && node.type.name === 'mention') {
            view.dispatch(view.state.tr.setSelection(NodeSelection.create(view.state.doc, nodePos)));
            return true;
          }
          return false;
        },
        handlePaste: (_view, event) => {
          const files = Array.from(event.clipboardData?.files ?? []).filter(isMediaFile);
          if (files.length === 0) return false;
          event.preventDefault();
          files.forEach((f) => void uploadAndInsert(f));
          return true;
        },
        handleDrop: (_view, event) => {
          // Any dropped file embeds (media inline, others as a file node).
          const files = Array.from((event as DragEvent).dataTransfer?.files ?? []);
          if (files.length === 0) return false;
          event.preventDefault();
          files.forEach((f) => void uploadAndInsert(f));
          return true;
        },
      },
    },
    [pageId],
  );

  const editorRef = useRef(editor);

  // Seed an IMPORTED page: if it arrived with body `content` but an empty Yjs doc (no collaborative
  // history yet), materialize that content into the doc ONCE — the edit then persists real doc_state,
  // so subsequent opens load normally. Guarded to an empty editor so it never clobbers real content.
  const seededRef = useRef(false);
  useEffect(() => {
    if (!editor || seededRef.current || !seedContent || !editor.isEmpty) return;
    seededRef.current = true;
    editor.commands.setContent(seedContent as never, true);
    // The seed is a one-time programmatic hydration of an imported page, NOT a user edit — drop it
    // from the collaborative undo history so the first ⌘Z can't wipe the freshly-imported page.
    // (A normal page loads via a non-tracked Yjs origin, so it's already absent from the stack.)
    yUndoPluginKey.getState(editor.state)?.undoManager?.clear();
  }, [editor, seedContent]);
  editorRef.current = editor;

  useEffect(() => {
    if (!restoreRef) return;
    restoreRef.current = (content: unknown) => {
      if (content && typeof content === 'object') editorRef.current?.chain().focus().setContent(content as Record<string, unknown>, true).run();
    };
    return () => {
      if (restoreRef) restoreRef.current = null;
    };
  }, [restoreRef, editor]);

  // Imperative comment commands for the page (add/remove a comment mark, scroll to a marked range).
  useEffect(() => {
    if (!commentCmdRef) return;
    commentCmdRef.current = {
      addMark: (id) => editorRef.current?.chain().focus().setCommentThread(id).run(),
      removeMark: (id) => editorRef.current?.commands.unsetCommentThread(id),
      scrollTo: (id) => {
        const el = editorRef.current?.view.dom.querySelector(`[data-thread-id="${id}"]`) as HTMLElement | null;
        el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      },
      commitPending: (id) => {
        const ed = editorRef.current;
        if (!ed) return;
        const range = pendingCommentKey.getState(ed.state);
        const tr = ed.state.tr.setMeta(pendingCommentKey, null); // remove the pending deco
        const type = ed.state.schema.marks.commentThread;
        // Anchor the real mark over the pending range as it stands NOW (mapped through any edits made
        // while the composer was open). If the text was deleted meanwhile the range collapses — the
        // thread still exists (with its quote), just without an in-text highlight.
        if (range && type && range.to > range.from) tr.addMark(range.from, range.to, type.create({ threadId: id }));
        ed.view.dispatch(tr);
      },
      clearPending: () => {
        const ed = editorRef.current;
        if (ed && pendingCommentKey.getState(ed.state)) ed.view.dispatch(ed.state.tr.setMeta(pendingCommentKey, null));
      },
    };
    return () => {
      if (commentCmdRef) commentCmdRef.current = null;
    };
  }, [commentCmdRef, editor]);

  // When the active thread or resolved-set changes, dispatch a no-op transaction so the comment-styling
  // decoration recomputes (it reads the live values via refs).
  useEffect(() => {
    const v = editorRef.current?.view;
    if (v) v.dispatch(v.state.tr.setMeta('commentDecoRefresh', true));
  }, [activeThreadId, resolvedThreadIds, editor]);

  // ---- autosave: persist the Yjs state + a JSON/text snapshot after local edits settle ----
  const doSave = useCallback(() => {
    const ed = editorRef.current;
    if (!ed) return;
    dirty.current = false;
    onSaveState?.('saving');
    wikiApi
      .saveWikiDoc(pageId, { doc: encodeDoc(ydoc), content: ed.getJSON(), text: ed.getText() })
      .then(() => onSaveState?.('saved'))
      .catch(() => onSaveState?.('error'));
  }, [pageId, ydoc, onSaveState]);

  useEffect(() => {
    const onUpdate = (_u: Uint8Array, origin: unknown) => {
      if (origin === REMOTE_ORIGIN || origin === 'wiki-load') return; // only save LOCAL edits
      dirty.current = true;
      onSaveState?.('idle');
      window.clearTimeout(saveTimer.current);
      saveTimer.current = window.setTimeout(doSave, 1200);
    };
    ydoc.on('update', onUpdate);
    return () => {
      ydoc.off('update', onUpdate);
      window.clearTimeout(saveTimer.current);
      if (dirty.current) doSave();
    };
  }, [ydoc, doSave, onSaveState]);

  const uploadAndInsert = useCallback(
    async (file: File) => {
      setUploading((n) => n + 1);
      try {
        const att = await wikiApi.uploadWikiAttachment(pageId, file);
        onAttachmentAdded?.();
        const src = wikiApi.wikiAttachmentUrl(att.id);
        // Images/videos embed inline (resizable); every other type embeds as a file node (PDF/HTML get a
        // resizable preview frame, the rest a download card).
        const node = isMediaFile(file)
          ? { type: 'image', attrs: { src } }
          : { type: 'wikiFile', attrs: { src, name: file.name, mime: file.type } };
        editorRef.current?.chain().focus().insertContent([node, { type: 'paragraph' }]).run();
      } finally {
        setUploading((n) => n - 1);
      }
    },
    [pageId, onAttachmentAdded],
  );

  const insertEmbed = useCallback((kind: 'item' | 'whiteboard', refId: string, label: string, href: string) => {
    editorRef.current?.chain().focus().insertContent([{ type: 'wikiEmbed', attrs: { kind, refId, label, href } }, { type: 'paragraph' }]).run();
  }, []);

  // ---- word-processor right-click menu (replaces the system context menu) ----
  showCtxRef.current = (clientX: number, clientY: number) => {
    const ed = editorRef.current;
    if (!ed) return;
    const coords = ed.view.posAtCoords({ left: clientX, top: clientY });
    let blame: BlockAuthor = {};
    if (coords) {
      const sel = ed.state.selection;
      // If the click is outside any active selection, drop the caret there so menu actions target it.
      if (sel.empty || coords.pos < sel.from || coords.pos > sel.to) ed.commands.setTextSelection(coords.pos);
      const a = ed.state.doc.resolve(coords.pos).parent.attrs as Record<string, string | undefined>;
      blame = { createdByName: a.createdByName, createdAt: a.createdAt, authorName: a.authorName, authorAt: a.authorAt };
    }
    setCtx({ x: clientX, y: clientY, blame });
  };

  // Open the link modal seeded with any link under the cursor (used by the toolbar + context menu).
  const openLink = () => {
    setCtx(null);
    setLinkModal({ url: (editorRef.current?.getAttributes('link').href as string) ?? '' });
  };

  // Start a new comment thread on a range (default: the current selection): mint an id, apply the mark
  // optimistically (in the Yjs doc), and ask the page to open the composer in the rail.
  const startThread = useCallback((range?: { from: number; to: number }) => {
    const ed = editorRef.current;
    if (!ed) return;
    const { from, to } = range ?? ed.state.selection;
    if (from === to) return;
    const quote = ed.state.doc.textBetween(from, to, ' ').slice(0, 300);
    const threadId = (crypto as Crypto).randomUUID();
    // Paint a PENDING highlight only (view-only decoration, not a doc mark) — so if the draft is
    // abandoned nothing is left behind. It's promoted to a real mark by commitPending() on submit.
    ed.view.dispatch(ed.state.tr.setMeta(pendingCommentKey, { from, to }));
    onRequestNewThreadRef.current?.(threadId, quote);
  }, []);

  // Gutter bubbles: a comment icon in the right margin aligned with the FIRST line of each comment mark,
  // reinforcing the highlight. Positioned within the (relative) editor root so it scrolls with the content;
  // recomputed only on layout changes (doc edits / resize), not on scroll (root-relative offsets are stable).
  useEffect(() => {
    if (!editor) return undefined;
    const compute = () => {
      const root = rootRef.current;
      const pm = editor.view.dom as HTMLElement;
      if (!root) return;
      const rootRect = root.getBoundingClientRect();
      const seen = new Set<string>();
      const bubbles: { id: string; top: number; left: number; resolved: boolean; active: boolean }[] = [];
      pm.querySelectorAll('.wiki-comment-mark[data-thread-id]').forEach((el) => {
        const id = el.getAttribute('data-thread-id');
        if (!id || seen.has(id)) return;
        seen.add(id);
        const r = (el as HTMLElement).getBoundingClientRect();
        // Anchor to the commented block's OWN right edge (the text column), NOT the editor box — so the
        // pin stays next to the content and doesn't drift to the far page margin (or hide behind the
        // comments rail) when the rail / pages sidebar resize the editor.
        const blockEl = ((el as HTMLElement).closest('p,h1,h2,h3,h4,h5,h6,li,blockquote,pre') as HTMLElement) || (el as HTMLElement);
        const colRight = blockEl.getBoundingClientRect().right;
        bubbles.push({
          id,
          top: r.top - rootRect.top,
          left: Math.min(colRight - rootRect.left + 10, rootRect.width - 26),
          resolved: resolvedThreadIds?.has(id) ?? false,
          active: id === activeThreadId,
        });
      });
      setGutterBubbles(bubbles);
    };
    // The global drag-handle is position:fixed and only repositions on hover, so it goes stale when the
    // editor is RESIZED (comments rail / pages sidebar opening shifts the content). Hide it on resize so
    // it doesn't linger in the wrong place; it reappears, correctly placed, on the next hover.
    const hideStaleDragHandle = () => {
      (editor.view.dom.parentElement ?? document).querySelector('.drag-handle:not(.wiki-add-comment)')?.classList.add('hide');
    };
    const onResize = () => { compute(); hideStaleDragHandle(); };
    compute();
    editor.on('update', compute);
    // Observe the editor ROOT (full-width, shrinks when the comments rail / pages sidebar open), NOT
    // .ProseMirror — the latter is max-width-capped and often does NOT resize, so it would miss the shift.
    const ro = new ResizeObserver(onResize);
    if (rootRef.current) ro.observe(rootRef.current);
    ro.observe(editor.view.dom);
    window.addEventListener('resize', onResize);
    return () => {
      editor.off('update', compute);
      ro.disconnect();
      window.removeEventListener('resize', onResize);
    };
  }, [editor, resolvedThreadIds, activeThreadId]);

  // A dim "+ Comment" affordance in the right gutter aligned with the row under the cursor (like Google
  // Docs). FULL-BLEED: the row is resolved from the cursor's Y at the CONTENT's horizontal centre, so it
  // fires anywhere across the row's full width (no dead zones between the text and the gutter). The button
  // is position:fixed (viewport coords, like the drag grip) and carries the `drag-handle` class so the
  // global-drag-handle extension's mouseout guard keeps the LEFT grip visible while you're on the button
  // (the grip doubles as a which-row cue). Cleared on wheel/leave so a stale pill never floats.
  const hoverFromRef = useRef<number | null>(null);
  useEffect(() => {
    if (!editor || !onRequestNewThread) return undefined;
    const root = rootRef.current;
    if (!root) return undefined;
    const pm = editor.view.dom as HTMLElement;
    const clear = () => { hoverFromRef.current = null; setHoverRow(null); };
    const onMove = (e: MouseEvent) => {
      const rootRect = root.getBoundingClientRect();
      const pmRect = pm.getBoundingClientRect();
      // Only within the content's vertical band and the editor's horizontal extent (full bleed).
      if (e.clientY < pmRect.top || e.clientY > pmRect.bottom || e.clientX < rootRect.left || e.clientX > rootRect.right) {
        if (hoverFromRef.current !== null) clear();
        return;
      }
      const info = editor.view.posAtCoords({ left: pmRect.left + pmRect.width / 2, top: e.clientY });
      if (!info) return; // between blocks — keep the current affordance rather than flicker
      const $pos = editor.state.doc.resolve(info.pos);
      if (!$pos.parent.isTextblock || $pos.parent.textContent.trim().length === 0) {
        if (hoverFromRef.current !== null) clear();
        return;
      }
      // No per-row "+ Comment" pill inside a table — every cell is its own textblock, so the pill would
      // pop up in every cell (unusable). Comment on table content by selecting text + the toolbar instead.
      for (let d = $pos.depth; d > 0; d -= 1) {
        const t = $pos.node(d).type.name;
        if (t === 'tableCell' || t === 'tableHeader' || t === 'table') {
          if (hoverFromRef.current !== null) clear();
          return;
        }
      }
      const from = $pos.start();
      const to = $pos.end();
      if (hoverFromRef.current === from) return; // same row — no churn
      let hasMark = false;
      editor.state.doc.nodesBetween(from, to, (n) => { if (n.isText && n.marks.some((m) => m.type.name === 'commentThread')) hasMark = true; });
      if (hasMark) { clear(); return; }
      hoverFromRef.current = from;
      // Anchor to the hovered block's own right edge (the text column is narrower than the editor box),
      // so the pill sits just past the row rather than out at the far page margin.
      const blockEl = editor.view.nodeDOM($pos.before()) as HTMLElement | null;
      const blockRect = blockEl?.getBoundingClientRect?.();
      const c = editor.view.coordsAtPos(from);
      const colRight = blockRect ? blockRect.right : pmRect.right;
      setHoverRow({ top: (blockRect ? blockRect.top : c.top) - 1, left: Math.min(colRight + 10, rootRect.right - 96), from, to });
    };
    root.addEventListener('mousemove', onMove);
    root.addEventListener('mouseleave', clear);
    window.addEventListener('wheel', clear, { passive: true });
    return () => {
      root.removeEventListener('mousemove', onMove);
      root.removeEventListener('mouseleave', clear);
      window.removeEventListener('wheel', clear);
    };
  }, [editor, onRequestNewThread]);

  return (
    <div ref={rootRef} className="relative flex flex-col">
      {gutterBubbles.map((b) => (
        <button
          key={b.id}
          type="button"
          onClick={() => onActivateThread?.(b.id)}
          style={{ position: 'absolute', top: b.top, left: b.left, zIndex: 5 }}
          className={cn(
            'flex h-[22px] w-[22px] items-center justify-center rounded-full rounded-bl-sm border shadow-sm transition-colors',
            b.resolved ? 'border-line bg-surface-raised text-fg-faint hover:text-fg-muted' : b.active ? 'border-accent bg-accent text-white' : 'border-warn/50 bg-warn/15 text-warn hover:bg-warn/25',
          )}
          title="Open this comment thread"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
          </svg>
        </button>
      ))}
      <WikiToolbar editor={editor} uploading={uploading > 0} onImageButton={() => fileRef.current?.click()} onEmbedButton={() => setEmbedOpen(true)} onLinkButton={openLink} onCommentButton={onRequestNewThread ? () => startThread() : undefined} />
      {hoverRow && onRequestNewThread ? (
        <button
          type="button"
          className="drag-handle wiki-add-comment"
          style={{ position: 'fixed', top: hoverRow.top, left: hoverRow.left }}
          onMouseDown={(e) => {
            e.preventDefault();
            startThread({ from: hoverRow.from, to: hoverRow.to });
            setHoverRow(null);
          }}
          title="Comment on this row"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
          <span>Comment</span>
        </button>
      ) : null}
      <input
        ref={fileRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          Array.from(e.target.files ?? []).forEach((f) => void uploadAndInsert(f));
          e.target.value = '';
        }}
      />
      <EditorContent editor={editor} className="wiki-editor py-4" />
      {ctx ? <ContextMenu x={ctx.x} y={ctx.y} blame={ctx.blame} editor={editor} onLink={openLink} onComment={onRequestNewThread ? () => startThread() : undefined} onClose={() => setCtx(null)} /> : null}
      {linkModal ? (
        <LinkModal
          initial={linkModal.url}
          onClose={() => setLinkModal(null)}
          onApply={(url) => {
            const ed = editorRef.current;
            if (ed) {
              if (url) ed.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
              else ed.chain().focus().extendMarkRange('link').unsetLink().run();
            }
            setLinkModal(null);
          }}
        />
      ) : null}
      {embedOpen ? <EmbedModal onInsert={insertEmbed} onClose={() => setEmbedOpen(false)} /> : null}
    </div>
  );
}

// Adds a `diff` attribute (→ data-diff) to every block type, so the history differ can tag blocks
// add/del and the CSS renders the change inline (green/red) in the rich read-only view.
const DiffAttributes = Extension.create({
  name: 'diffAttributes',
  addGlobalAttributes() {
    return [
      {
        types: ['paragraph', 'heading', 'blockquote', 'codeBlock', 'bulletList', 'orderedList', 'taskList', 'radioGroup', 'image', 'wikiEmbed', 'checklistProgress', 'horizontalRule', 'table'],
        attributes: {
          diff: { default: null, parseHTML: (el) => el.getAttribute('data-diff'), renderHTML: (attrs) => (attrs.diff ? { 'data-diff': attrs.diff } : {}) },
        },
      },
    ];
  },
});

/**
 * A read-only render of a wiki document (ProseMirror JSON) using the SAME visual extensions + node
 * views as the editor — so a history revision / preview looks exactly like the page (images, tables,
 * checklists, embeds, mentions all render), minus the collaborative + editing machinery. When the
 * content is a diff document (blocks tagged attrs.diff), DiffAttributes surfaces it as data-diff.
 */
export function WikiReadOnly({ content, members }: { content: unknown; members: Member[] }) {
  const membersRef = useRef(members);
  membersRef.current = members;
  const editor = useEditor(
    {
      editable: false,
      extensions: [
        StarterKit.configure({ heading: { levels: [1, 2, 3] }, codeBlock: { HTMLAttributes: { class: 'md-code' } } }),
        Link.configure({ openOnClick: false, HTMLAttributes: { target: '_blank', rel: 'noreferrer noopener', class: 'md-link' } }),
        WikiImage,
        WikiFile,
        WikiEmbed,
        ChecklistProgress,
        RadioGroup,
        RadioItem,
        Table.configure({ resizable: false, HTMLAttributes: { class: 'wiki-table' } }),
        TableRow,
        TableHeader,
        TableCell,
        TaskList,
        TaskItem.configure({ nested: true }),
        Indent,
        createWikiMention(() => membersRef.current),
        WikiCommentMark,
        DiffAttributes,
      ],
      content: content && typeof content === 'object' ? (content as Record<string, unknown>) : { type: 'doc', content: [] },
    },
    [content],
  );
  return <EditorContent editor={editor} className="md-content wiki-content wiki-readonly" />;
}

const isMediaFile = (f: File) => f.type.startsWith('image/') || f.type.startsWith('video/');

/**
 * Maps pasted rich HTML (e.g. a "select all → copy" from a rendered checklist page) onto wiki editor
 * constructs that TipTap doesn't recognise natively:
 *  - <label><input type=checkbox [checked]>text</label>  → task-list items (grouped into a taskList)
 *  - <section> boundaries / border-top rules             → horizontal-rule dividers
 *  - border-left callout boxes                           → blockquotes
 * Everything else (headings, bold via font-weight, links, lists, tables) is left for ProseMirror's own
 * clipboard parser. A fast-path returns the HTML untouched when none of these patterns are present, so
 * ordinary paste (and internal copy/paste) is never disturbed.
 */
function mapPastedHtml(html: string): string {
  // Fast-path: leave ordinary/internal paste untouched unless one of our mappable patterns is present.
  const CHECK_GLYPHS = '☐☑☒✅✔✓□▢◻◽⬜○◯⭕❏';
  const glyphRe = new RegExp(`>[\\s\\u00A0]*(?:[${CHECK_GLYPHS}]|\\[[ xX]?\\])[\\s\\u00A0]`);
  if (
    !/type=["']?checkbox/i.test(html) &&
    !/data-tag=["']?to-do/i.test(html) &&
    !/border-top|border-left|border-inline-start|<section/i.test(html) &&
    !glyphRe.test(html)
  )
    return html;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('script, style').forEach((e) => e.remove());

  // border-left / border-inline-start callouts → blockquotes.
  doc.querySelectorAll('div[style*="border-left"], div[style*="border-inline-start"], .note').forEach((el) => {
    const bq = doc.createElement('blockquote');
    const p = doc.createElement('p');
    p.innerHTML = el.innerHTML.trim();
    bq.appendChild(p);
    el.replaceWith(bq);
  });

  // OneNote-for-web / Microsoft Graph note-tags: a to-do checkbox is `data-tag="to-do"` (unchecked) or
  // `data-tag="to-do:completed"` (checked) on a block/span. This is the ONLY machine-readable checkbox
  // signal these ecosystems emit — the desktop apps drop the checkbox on copy, and Apple Notes exports
  // checklists as indistinguishable plain bullets, so neither of those can be reconstructed here.
  doc.querySelectorAll('[data-tag^="to-do"]').forEach((el) => {
    const checked = /:completed/i.test(el.getAttribute('data-tag') || '');
    const li = doc.createElement('li');
    li.setAttribute('data-type', 'taskItem');
    li.setAttribute('data-checked', checked ? 'true' : 'false');
    const p = doc.createElement('p');
    // The tag may sit on the block itself or on an inner span; take the block's text either way.
    p.innerHTML = (el.tagName === 'SPAN' ? (el.closest('li,p,div') ?? el) : el).innerHTML.trim() || '&nbsp;';
    const host = el.tagName === 'SPAN' ? el.closest('li,p,div') ?? el : el;
    host.replaceWith(li);
  });

  // Leading checkbox glyph (☐/☑/▢/○/[ ]/[x] …) at the start of a list item or line → task item. Covers
  // markdown-ish and glyph-bearing sources; the checked variants (☑☒✅✔✓/[x]) carry the checked state.
  const CHECKED = new Set(['☑', '☒', '✅', '✔', '✓']);
  const leadRe = new RegExp(`^[\\s\\u00A0]*(\\[[ xX]?\\]|[${CHECK_GLYPHS}])[\\s\\u00A0]+`);
  doc.querySelectorAll('li, p, div').forEach((el) => {
    if (el.querySelector('ul, ol, li, p, div, table')) return; // only leaf lines
    if (el.closest('li[data-type="taskItem"]')) return;
    const walker = doc.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    let tn = walker.nextNode();
    while (tn && !(tn.textContent && tn.textContent.trim())) tn = walker.nextNode();
    if (!tn) return;
    const m = (tn.textContent || '').match(leadRe);
    if (!m) return;
    const g = m[1].trim();
    const checked = CHECKED.has(g) || /\[[xX]\]/.test(g);
    tn.textContent = (tn.textContent || '').replace(leadRe, ''); // strip the glyph
    const li = doc.createElement('li');
    li.setAttribute('data-type', 'taskItem');
    li.setAttribute('data-checked', checked ? 'true' : 'false');
    const p = doc.createElement('p');
    p.innerHTML = el.innerHTML.trim() || '&nbsp;';
    el.replaceWith(li);
    li.appendChild(p);
  });

  // Each element that directly wraps a checkbox → a taskItem <li>; the checkbox's checked state carries.
  doc.querySelectorAll('input[type="checkbox"]').forEach((cb) => {
    // Skip checkboxes that are ALREADY inside a TipTap task item — this HTML came from copying within the
    // editor and is well-formed; re-wrapping it doubles the item (empty checkbox + separated text). Only
    // map RAW external checkboxes (pasted HTML / other apps).
    if (cb.closest('li[data-type="taskItem"], li[data-checked]')) return;
    const line = cb.closest('label') ?? cb.parentElement;
    if (!line) return;
    const checked = cb.hasAttribute('checked') || (cb as HTMLInputElement).checked;
    cb.remove();
    const li = doc.createElement('li');
    li.setAttribute('data-type', 'taskItem');
    li.setAttribute('data-checked', checked ? 'true' : 'false');
    const p = doc.createElement('p');
    p.innerHTML = (line === cb.parentElement ? line.innerHTML : line.innerHTML).trim() || '&nbsp;';
    li.appendChild(p);
    line.replaceWith(li);
  });

  // Group adjacent taskItem <li>s into a single taskList <ul>.
  const seen = new Set<Element>();
  doc.querySelectorAll('li[data-type="taskItem"]').forEach((li) => {
    if (seen.has(li) || (li.parentElement && li.parentElement.getAttribute('data-type') === 'taskList')) return;
    const ul = doc.createElement('ul');
    ul.setAttribute('data-type', 'taskList');
    li.replaceWith(ul);
    ul.appendChild(li);
    seen.add(li);
    let next: ChildNode | null = ul.nextSibling;
    while (next) {
      const after: ChildNode | null = next.nextSibling;
      if (next.nodeType === 3 /* text */ && !next.textContent?.trim()) { next.remove(); next = after; continue; }
      if (next.nodeType === 1 /* element */ && (next as Element).matches('li[data-type="taskItem"]')) {
        ul.appendChild(next);
        seen.add(next as Element);
        next = after;
        continue;
      }
      break;
    }
  });

  // Insert a divider before each section after the first (the source separates sections with a rule).
  doc.querySelectorAll('section').forEach((s, i) => { if (i > 0) s.parentElement?.insertBefore(doc.createElement('hr'), s); });

  // Drop source lists left empty after their items were lifted into task lists.
  doc.querySelectorAll('ul, ol').forEach((l) => { if (!l.querySelector('li')) l.remove(); });

  return doc.body.innerHTML;
}

function fmt(iso?: string) {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

// ---- toolbar ----
/**
 * Read collaborative (Yjs) undo/redo availability off the live editor state. The Collaboration
 * extension owns a single `Y.UndoManager` scoped to THIS user's own edits — so undo reverts only
 * your changes and never a collaborator's, and it stays correct across concurrent remote edits
 * (the whole reason we don't use ProseMirror's built-in history here). Reading the stack depth lets
 * the toolbar buttons enable/disable accurately; the values refresh because Tiptap re-renders on
 * every transaction (edits, undo, and redo all dispatch one).
 */
function readUndo(editor: ReturnType<typeof useEditor>): { canUndo: boolean; canRedo: boolean } {
  const um = editor ? yUndoPluginKey.getState(editor.state)?.undoManager : null;
  return { canUndo: !!um && um.undoStack.length > 0, canRedo: !!um && um.redoStack.length > 0 };
}

function WikiToolbar({
  editor,
  uploading,
  onImageButton,
  onEmbedButton,
  onLinkButton,
  onCommentButton,
}: {
  editor: ReturnType<typeof useEditor>;
  uploading: boolean;
  onImageButton: () => void;
  onEmbedButton: () => void;
  onLinkButton: () => void;
  onCommentButton?: () => void;
}) {
  const btn = (active: boolean) => cn('flex h-7 w-7 items-center justify-center rounded-md transition-colors', active ? 'bg-accent/15 text-accent' : 'text-fg-muted hover:bg-layer-strong hover:text-fg');
  const run = (fn: () => void) => (e: React.MouseEvent) => {
    e.preventDefault();
    fn();
  };
  const disabled = !editor;
  const { canUndo, canRedo } = readUndo(editor);
  // Indent / outdent: nest inside a list, else bump the block indent level (mirrors Tab / ⌘]).
  const indent = () => {
    if (!editor) return;
    if (editor.isActive('listItem') || editor.isActive('taskItem')) { if (!editor.commands.sinkListItem('listItem')) editor.commands.sinkListItem('taskItem'); }
    else editor.commands.indentBlocks();
    editor.commands.focus();
  };
  const outdent = () => {
    if (!editor) return;
    if (editor.isActive('listItem') || editor.isActive('taskItem')) { if (!editor.commands.liftListItem('listItem')) editor.commands.liftListItem('taskItem'); }
    else editor.commands.outdentBlocks();
    editor.commands.focus();
  };
  const inTable = !!editor?.isActive('table');
  const imageSel = !!editor?.isActive('image');
  // The sticky table header pins BELOW the whole toolbar block — which grows a row when the table/image
  // sub-toolbar appears. Publish the block's live height as `--wiki-toolbar-h` so the header offset
  // (index.scss) tracks it instead of covering the header behind the sub-toolbar.
  const barRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = barRef.current;
    if (!el) return undefined;
    const set = () => document.documentElement.style.setProperty('--wiki-toolbar-h', `${el.offsetHeight}px`);
    set();
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => {
      ro.disconnect();
      document.documentElement.style.removeProperty('--wiki-toolbar-h');
    };
  }, []);
  return (
    <div ref={barRef} className="sticky top-0 z-20 border-b border-line bg-surface">
      <div className="wiki-col flex flex-wrap items-center gap-0.5 py-1">
      <div className={cn('flex flex-wrap items-center gap-0.5', disabled && 'pointer-events-none opacity-40')}>
        <button type="button" className={cn(btn(false), 'disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-fg-muted')} disabled={!canUndo} onMouseDown={run(() => editor?.chain().focus().undo().run())} title="Undo (⌘Z)"><IcoUndo /></button>
        <button type="button" className={cn(btn(false), 'disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-fg-muted')} disabled={!canRedo} onMouseDown={run(() => editor?.chain().focus().redo().run())} title="Redo (⌘⇧Z)"><IcoRedo /></button>
        <span className="mx-0.5 h-4 w-px bg-line" />
        <button type="button" className={btn(!!editor?.isActive('bold'))} onMouseDown={run(() => editor?.chain().focus().toggleBold().run())} title="Bold (⌘B)"><IcoBold /></button>
        <button type="button" className={btn(!!editor?.isActive('italic'))} onMouseDown={run(() => editor?.chain().focus().toggleItalic().run())} title="Italic (⌘I)"><IcoItalic /></button>
        <button type="button" className={btn(!!editor?.isActive('strike'))} onMouseDown={run(() => editor?.chain().focus().toggleStrike().run())} title="Strikethrough"><IcoStrike /></button>
        <button type="button" className={btn(!!editor?.isActive('code'))} onMouseDown={run(() => editor?.chain().focus().toggleCode().run())} title="Inline code"><IcoCode /></button>
        <span className="mx-0.5 h-4 w-px bg-line" />
        <button type="button" className={btn(!!editor?.isActive('heading', { level: 1 }))} onMouseDown={run(() => editor?.chain().focus().toggleHeading({ level: 1 }).run())} title="Heading 1"><IcoH1 /></button>
        <button type="button" className={btn(!!editor?.isActive('heading', { level: 2 }))} onMouseDown={run(() => editor?.chain().focus().toggleHeading({ level: 2 }).run())} title="Heading 2"><IcoH2 /></button>
        <button type="button" className={btn(!!editor?.isActive('heading', { level: 3 }))} onMouseDown={run(() => editor?.chain().focus().toggleHeading({ level: 3 }).run())} title="Heading 3"><IcoH3 /></button>
        <span className="mx-0.5 h-4 w-px bg-line" />
        <button type="button" className={btn(!!editor?.isActive('bulletList'))} onMouseDown={run(() => editor?.chain().focus().toggleBulletList().run())} title="Bullet list"><IcoBullet /></button>
        <button type="button" className={btn(!!editor?.isActive('orderedList'))} onMouseDown={run(() => editor?.chain().focus().toggleOrderedList().run())} title="Numbered list"><IcoOrdered /></button>
        <button type="button" className={btn(!!editor?.isActive('taskList'))} onMouseDown={run(() => editor?.chain().focus().toggleTaskList().run())} title="Checklist"><IcoTask /></button>
        <button type="button" className={btn(false)} onMouseDown={run(outdent)} title="Outdent (⇧Tab or ⌘[)"><IcoOutdent /></button>
        <button type="button" className={btn(false)} onMouseDown={run(indent)} title="Indent (Tab or ⌘])"><IcoIndent /></button>
        <button type="button" className={btn(!!editor?.isActive('blockquote'))} onMouseDown={run(() => editor?.chain().focus().toggleBlockquote().run())} title="Quote"><IcoQuote /></button>
        <button type="button" className={btn(!!editor?.isActive('codeBlock'))} onMouseDown={run(() => editor?.chain().focus().toggleCodeBlock().run())} title="Code block"><IcoCodeBlock /></button>
        <span className="mx-0.5 h-4 w-px bg-line" />
        <button type="button" className={btn(!!editor?.isActive('link'))} onMouseDown={run(onLinkButton)} title="Link"><IcoLink /></button>
        {onCommentButton ? <button type="button" className={btn(false)} onMouseDown={run(onCommentButton)} disabled={!!editor?.state.selection.empty} title="Comment on selection"><IcoComment /></button> : null}
        <button type="button" className={btn(!!editor?.isActive('table'))} onMouseDown={run(() => editor?.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run())} title="Insert table"><IcoTable /></button>
        <button type="button" className={btn(false)} onMouseDown={run(onImageButton)} title="Upload a file (image, video, PDF, doc…)"><IcoImage /></button>
        <button type="button" className={btn(false)} onMouseDown={run(onEmbedButton)} title="Insert an item or whiteboard"><IcoEmbed /></button>
        <button type="button" className={btn(!!editor?.isActive('radioItem'))} onMouseDown={run(() => editor?.chain().focus().insertContent(RADIO_GROUP_JSON).run())} title="Radio group (single choice)"><IcoRadio /></button>
        <button type="button" className={btn(false)} onMouseDown={run(() => editor?.chain().focus().insertContent({ type: 'checklistProgress' }).run())} title="Checklist completion widget"><IcoProgress /></button>
      </div>
      {uploading ? <Spinner className="ml-auto h-3.5 w-3.5 text-fg-faint" /> : null}
      </div>
      {/* Contextual sub-toolbar: a second row UNDER the main toolbar (never a floating menu over the
          content) — table controls while the caret is in a table, image controls while one is selected. */}
      {inTable && editor ? <TableSubToolbar editor={editor} /> : imageSel && editor ? <ImageSubToolbar editor={editor} /> : null}
    </div>
  );
}

// ---- image controls (sub-toolbar shown while an image is selected) ----
function ImageSubToolbar({ editor }: { editor: NonNullable<ReturnType<typeof useEditor>> }) {
  const setW = (w: number | null) => (e: React.MouseEvent) => {
    e.preventDefault();
    editor.chain().focus().updateAttributes('image', { width: w }).run();
  };
  const pill = 'rounded-md px-2.5 py-1 text-[11px] font-semibold text-fg-soft transition-colors hover:bg-layer-strong hover:text-fg';
  return (
    <div className="wiki-col flex flex-wrap items-center gap-0.5 border-t border-line py-1">
      <span className="mr-1 text-[10px] font-semibold uppercase tracking-wide text-fg-faint">Image</span>
      <button type="button" className={pill} onMouseDown={setW(280)} title="Small">Small</button>
      <button type="button" className={pill} onMouseDown={setW(440)} title="Medium">Medium</button>
      <button type="button" className={pill} onMouseDown={setW(640)} title="Large">Large</button>
      <button type="button" className={pill} onMouseDown={setW(null)} title="Reset to natural width">Fit</button>
      <span className="mx-0.5 h-4 w-px bg-line" />
      <button type="button" className="rounded-md px-2.5 py-1 text-[11px] font-semibold text-fg-muted transition-colors hover:bg-danger/15 hover:text-danger" onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().deleteSelection().run(); }} title="Remove image">Remove</button>
    </div>
  );
}

// ---- table controls: a compact icon sub-toolbar (rendered under the main toolbar while in a table) ----
function TableSubToolbar({ editor }: { editor: NonNullable<ReturnType<typeof useEditor>> }) {
  const chain = () => editor.chain().focus();
  const b = (danger?: boolean) => cn('flex h-7 w-7 items-center justify-center rounded-md transition-colors', danger ? 'text-fg-muted hover:bg-danger/15 hover:text-danger' : 'text-fg-muted hover:bg-layer-strong hover:text-fg');
  const run = (fn: () => void) => (e: React.MouseEvent) => {
    e.preventDefault();
    fn();
  };
  const sep = <span className="mx-0.5 h-4 w-px bg-line" />;
  return (
    <div className="wiki-col flex flex-wrap items-center gap-0.5 border-t border-line py-1">
      <span className="mr-1 text-[10px] font-semibold uppercase tracking-wide text-fg-faint">Table</span>
      <button type="button" className={b()} onMouseDown={run(() => chain().addRowBefore().run())} title="Insert row above"><IcoRowAbove /></button>
      <button type="button" className={b()} onMouseDown={run(() => chain().addRowAfter().run())} title="Insert row below"><IcoRowBelow /></button>
      <button type="button" className={b(true)} onMouseDown={run(() => chain().deleteRow().run())} title="Delete row"><IcoRowDel /></button>
      {sep}
      <button type="button" className={b()} onMouseDown={run(() => chain().addColumnBefore().run())} title="Insert column left"><IcoColLeft /></button>
      <button type="button" className={b()} onMouseDown={run(() => chain().addColumnAfter().run())} title="Insert column right"><IcoColRight /></button>
      <button type="button" className={b(true)} onMouseDown={run(() => chain().deleteColumn().run())} title="Delete column"><IcoColDel /></button>
      {sep}
      <button type="button" className={b()} onMouseDown={run(() => chain().toggleHeaderRow().run())} title="Toggle header row"><IcoHeader /></button>
      <button type="button" className={b()} onMouseDown={run(() => chain().mergeOrSplit().run())} title="Merge / split cells"><IcoMerge /></button>
      {sep}
      <button type="button" className={b(true)} onMouseDown={run(() => chain().deleteTable().run())} title="Delete table"><IcoTableDel /></button>
    </div>
  );
}

// ---- word-processor context menu ----
function ContextMenu({ x, y, blame, editor, onLink, onComment, onClose }: { x: number; y: number; blame: BlockAuthor; editor: ReturnType<typeof useEditor>; onLink: () => void; onComment?: () => void; onClose: () => void }) {
  useEffect(() => {
    const close = () => onClose();
    window.addEventListener('pointerdown', close);
    window.addEventListener('scroll', close, true);
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointerdown', close);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  if (!editor) return null;
  const hasSel = !editor.state.selection.empty;
  const act = (fn: () => void) => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    fn();
    onClose();
  };
  const clip = (cmd: 'copy' | 'cut') => () => { try { document.execCommand(cmd); } catch { /* ignore */ } };
  const chain = () => editor.chain().focus();
  const isLink = editor.isActive('link');
  const pasteText = () => void navigator.clipboard?.readText?.().then((t) => t && editor.chain().focus().insertContent(t).run()).catch(() => undefined);
  const mod = typeof navigator !== 'undefined' && /mac/i.test(navigator.platform) ? '⌘' : 'Ctrl ';

  const CIco = ({ d }: { d: string }) => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"><path d={d} /></svg>
  );
  const Row = ({ icon, label, shortcut, onClick, disabled }: { icon?: React.ReactNode; label: string; shortcut?: string; onClick: () => void; disabled?: boolean }) => (
    <button
      type="button"
      disabled={disabled}
      onMouseDown={disabled ? undefined : act(onClick)}
      className={cn('flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-left text-[13px] transition-colors', disabled ? 'cursor-default text-fg-ghost' : 'text-fg-soft hover:bg-layer')}
    >
      <span className="flex h-4 w-4 shrink-0 items-center justify-center text-fg-muted">{icon}</span>
      <span className="flex-1">{label}</span>
      {shortcut ? <span className="text-[11px] tracking-wide text-fg-faint">{shortcut}</span> : null}
    </button>
  );
  const Sep = () => <div className="my-1 h-px bg-line" />;

  // A focused word-processor menu (Google-Docs style): clipboard + link + the block's attribution.
  // Formatting/turn-into lives in the toolbar and the "/" menu, not here.
  return (
    <div
      className="fixed z-[120] w-60 rounded-xl border border-line bg-surface-raised p-1 text-fg shadow-xl"
      style={{ left: Math.min(x, window.innerWidth - 250), top: Math.min(y, window.innerHeight - 240) }}
      onPointerDown={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.preventDefault()}
    >
      <Row
        icon={
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="6" cy="6" r="3" />
            <circle cx="6" cy="18" r="3" />
            <path d="M20 4 8.12 15.88M14.47 14.48 20 20M8.12 8.12 12 12" />
          </svg>
        }
        label="Cut"
        shortcut={`${mod}X`}
        onClick={() => document.execCommand('cut')}
        disabled={!hasSel}
      />
      <Row icon={<CIco d="M9 9h11v11H9zM5 15V4h11" />} label="Copy" shortcut={`${mod}C`} onClick={() => document.execCommand('copy')} disabled={!hasSel} />
      <Row icon={<CIco d="M9 4h6v3H9zM8 5H5v15h14V5h-3M9 12h6M9 16h4" />} label="Paste" shortcut={`${mod}V`} onClick={pasteText} />
      <Sep />
      <Row icon={<CIco d="M10 13a5 5 0 0 0 7.5.5l2-2a5 5 0 0 0-7-7l-1.2 1.2M14 11a5 5 0 0 0-7.5-.5l-2 2a5 5 0 0 0 7 7l1.2-1.2" />} label={isLink ? 'Edit link' : 'Insert link'} onClick={onLink} />
      {isLink ? <Row icon={<CIco d="M9 17H7A5 5 0 0 1 7 7h2M15 7h2a5 5 0 0 1 0 10h-2M4 4l16 16" />} label="Remove link" onClick={() => chain().unsetLink().run()} /> : null}
      {onComment ? <Row icon={<CIco d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />} label="Comment" onClick={onComment} disabled={!hasSel} /> : null}
      {blame.createdByName || blame.authorName ? (
        <>
          <Sep />
          <div className="px-2.5 py-1.5 text-[11px] leading-relaxed text-fg-faint">
            <div>Created by <span className="text-fg-muted">{blame.createdByName || '—'}</span></div>
            <div>Edited by <span className="text-fg-muted">{blame.authorName || blame.createdByName || '—'}</span> · {fmt(blame.authorAt || blame.createdAt)}</div>
          </div>
        </>
      ) : null}
    </div>
  );
}

// ---- insert-embed modal (board item / whiteboard) ----
function EmbedModal({ onInsert, onClose }: { onInsert: (kind: 'item' | 'whiteboard', refId: string, label: string, href: string) => void; onClose: () => void }) {
  const [tab, setTab] = useState<'item' | 'whiteboard'>('item');
  const [q, setQ] = useState('');
  const [items, setItems] = useState<{ id: string; key: string; title: string; board_id: string }[]>([]);
  const [whiteboards, setWhiteboards] = useState<{ id: string; name: string }[]>([]);
  const [slugByBoard, setSlugByBoard] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    boardApi.listBoards().then((bs) => setSlugByBoard(Object.fromEntries(bs.map((b) => [b.id, b.slug])))).catch(() => {});
    canvasApi.listWhiteboards().then((ws) => setWhiteboards(ws.map((w) => ({ id: w.id, name: w.name })))).catch(() => {});
  }, []);

  useEffect(() => {
    if (tab !== 'item') return;
    const t = window.setTimeout(() => {
      setLoading(true);
      // /items/search returns a ListResult → the array is under `data`, not `items`.
      http
        .post<{ data?: { id: string; key: string; title: string; board_id: string }[] }>('/items/search', { text: q, limit: 10 })
        .then((r) => setItems(r.data.data ?? []))
        .catch(() => setItems([]))
        .finally(() => setLoading(false));
    }, 200);
    return () => window.clearTimeout(t);
  }, [q, tab]);

  const pickItem = (it: { id: string; key: string; title: string; board_id: string }) => {
    const slug = slugByBoard[it.board_id];
    onInsert('item', it.id, `${it.key} — ${it.title}`, slug ? `/${slug}/${it.key}` : `/?item=${it.key}`);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-start justify-center bg-black/40 pt-[16vh]" onPointerDown={onClose}>
      <div className="w-[380px] rounded-2xl border border-line bg-surface-raised p-3 shadow-2xl" onPointerDown={(e) => e.stopPropagation()}>
        <div className="mb-2 flex items-center gap-1 rounded-lg bg-layer p-0.5 text-[12px] font-semibold">
          {(['item', 'whiteboard'] as const).map((t) => (
            <button key={t} type="button" onClick={() => setTab(t)} className={cn('flex-1 rounded-md px-2 py-1.5 transition-colors', tab === t ? 'bg-surface-raised text-fg-strong shadow-sm' : 'text-fg-muted hover:text-fg')}>
              {t === 'item' ? 'Board item' : 'Whiteboard'}
            </button>
          ))}
        </div>
        {tab === 'item' ? (
          <>
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search items by title or key…" className="mb-1.5 w-full rounded-lg border border-line bg-surface-inset px-3 py-2 text-[13px] text-fg outline-none focus:border-line-strong" />
            <div className="max-h-[300px] overflow-auto">
              {loading ? (
                <div className="flex justify-center py-5"><Spinner className="h-4 w-4 text-fg-faint" /></div>
              ) : items.length === 0 ? (
                <div className="px-2 py-4 text-center text-[12px] text-fg-faint">{q ? 'No matches' : 'Type to search items'}</div>
              ) : (
                items.map((it) => (
                  <button key={it.id} type="button" onClick={() => pickItem(it)} className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-[13px] transition-colors hover:bg-layer">
                    <span className="shrink-0 rounded bg-brand/15 px-1.5 py-0.5 text-[10px] font-bold text-brand">{it.key}</span>
                    <span className="min-w-0 flex-1 truncate text-fg-soft">{it.title}</span>
                  </button>
                ))
              )}
            </div>
          </>
        ) : (
          <div className="max-h-[340px] overflow-auto">
            {whiteboards.length === 0 ? (
              <div className="px-2 py-4 text-center text-[12px] text-fg-faint">No whiteboards</div>
            ) : (
              whiteboards.map((w) => (
                <button key={w.id} type="button" onClick={() => { onInsert('whiteboard', w.id, w.name, `/whiteboards/${w.id}`); onClose(); }} className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-[13px] transition-colors hover:bg-layer">
                  <span className="shrink-0 text-violet">▦</span>
                  <span className="min-w-0 flex-1 truncate text-fg-soft">{w.name}</span>
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---- link modal (a styled dialog instead of window.prompt) ----
function LinkModal({ initial, onApply, onClose }: { initial: string; onApply: (url: string) => void; onClose: () => void }) {
  const [url, setUrl] = useState(initial || 'https://');
  const apply = () => onApply(url.trim());
  return (
    <div className="fixed inset-0 z-[110] flex items-start justify-center bg-black/40 pt-[18vh]" onPointerDown={onClose}>
      <div className="w-[420px] rounded-2xl border border-line bg-surface-raised p-4 shadow-2xl" onPointerDown={(e) => e.stopPropagation()}>
        <div className="mb-2 text-[13px] font-semibold text-fg-strong">{initial ? 'Edit link' : 'Insert link'}</div>
        <input
          autoFocus
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') apply(); if (e.key === 'Escape') onClose(); }}
          placeholder="https://…"
          className="w-full rounded-lg border border-line bg-surface-inset px-3 py-2 text-[13px] text-fg outline-none focus:border-line-strong"
        />
        <div className="mt-3 flex items-center justify-between">
          {initial ? (
            <button type="button" onClick={() => onApply('')} className="text-[12px] font-medium text-danger hover:underline">Remove link</button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="rounded-lg px-3 py-1.5 text-[12px] text-fg-muted transition-colors hover:bg-layer hover:text-fg">Cancel</button>
            <button type="button" onClick={apply} className="rounded-lg bg-brand px-3 py-1.5 text-[12px] font-semibold text-white transition-opacity hover:opacity-90">Apply</button>
          </div>
        </div>
      </div>
    </div>
  );
}
